<template>
  <div class="user-profile card">
    <div class="grid grid-cols-1 gap-8 lg:grid-cols-3">
      <!-- Avatar Section -->
      <div class="lg:col-span-1">
        <div class="flex flex-col items-center gap-6 p-4 border border-gray-200 rounded-lg">
          <div class="relative">
            <BaseAvatar
              :src="user.avatar"
              size="6rem"
              class="ring-4 ring-primary-100"
            />
            <div class="absolute bottom-0 right-0">
              <BaseButton
                variant="secondary"
                size="sm"
                icon-class="i-mdi-camera"
                class="rounded-full p-2"
                @click="openAvatarUploadModal = true"
              />
            </div>
          </div>
          <p class="text-sm text-gray-500 text-center">Click the camera icon to change your profile picture</p>
        </div>
      </div>

      <!-- Profile Information -->
      <div class="lg:col-span-2">
        <div class="space-y-6">
          <div class="border-b border-gray-200 pb-6">
            <h2 class="text-lg font-medium text-gray-900 mb-4">Personal Information</h2>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <BaseInput
                v-model="user.name"
                label="Full Name"
                placeholder="Enter your full name"
              />
              <BaseInput
                v-model="user.email"
                label="Email"
                disabled
                class="opacity-75"
              />
              <BaseInput
                v-model="user.phone"
                label="Phone Number"
                placeholder="Enter your phone number"
              />
            </div>
          </div>

          <div class="border-b border-gray-200 pb-6">
            <h2 class="text-lg font-medium text-gray-900 mb-4">Security</h2>
            <div class="space-y-4">
              <BaseInputPassword
                v-model="user.newPassword"
                label="New Password"
                placeholder="Enter new password"
              />
              <p class="text-sm text-gray-500">Leave blank if you don't want to change your password</p>
            </div>
          </div>

          <div class="flex justify-end">
            <BaseButton
              variant="primary"
              class="min-w-[120px]"
              @click="saveProfile"
            >
              Save Changes
            </BaseButton>
          </div>
        </div>
      </div>
    </div>

    <AppModal 
      v-model="openAvatarUploadModal" 
      title="Upload Profile Picture" 
      @close="openAvatarUploadModal = false"
    >
      <div class="p-4">
        <AppFileUpload
          ref="fileUpload"
          :storage-path="`${user.id}/avatars/`"
          accept="image/*"
          @on-file-upload-completed="fileUploadCompleted"
        />
      </div>
    </AppModal>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import AppFileUpload from '@/components/app/AppFileUpload.vue'
import { useAuth } from '@/composables/useAuth'
import { useNotification } from '@kyvg/vue3-notification'
import BaseInputPassword from '@/components/base/BaseInputPassword.vue'
import AppModal from '@/components/app/AppModal.vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import UserService from '@/services/UserService'
import { useMutation } from '@tanstack/vue-query'
import { User } from '@/models/user'

const { currentUser } = useAuth()
const { notify } = useNotification()
const userService = new UserService()

interface UserProfile {
  id: string
  name: string
  email: string
  phone: string
  newPassword: string
  avatar: string
}

const user = ref<UserProfile>({
  id: '',
  name: '',
  email: '',
  phone: '',
  newPassword: '',
  avatar: ''
})

const openAvatarUploadModal = ref(false)

const { mutate: updateProfile } = useMutation({
  mutationFn: (user: User) => userService.update(user.id, user),
  onSuccess: () => {
    notify({
      title: 'Success',
      text: 'User profile updated successfully!',
      type: 'success'
    })
  },
  onError: () => {
    notify({
      title: 'Error',
      text: 'Error updating profile, please try again.',
      type: 'error'
    })
  }
})

const saveProfile = async () => {
  try {
    await updateProfile(user.value)
    notify({
      title: 'Success',
      text: 'User profile updated successfully!',
      type: 'success'
    })
  } catch (error) {
    notify({
      title: 'Error',
      text: 'Error updating profile, please try again.',
      type: 'error'
    })
  }
}

const fileUploadCompleted = async (url: string) => {
  user.value.avatar = url

  // save the avatar to the user
  try {
    await updateProfile(user.value)
    notify({
      title: 'Success',
      text: 'Avatar uploaded successfully!',
      type: 'success'
    })
  } catch (error) {
    notify({
      title: 'Error',
      text: 'Error uploading avatar, please try again.',
      type: 'error'
    })
  }
}

onMounted(() => {
  if (currentUser.value) {
    user.value = {
      ...user.value,
      ...currentUser.value
    }
  }
})
</script>
